import BankFiltersProvider, {
  useBankFilters,
} from '@/data/context/BankFiltersContext';
import useAuth from '@/data/hook/useAuth';
import { isSuperAdmin } from '@/functions/auth';
import Activity from '@/models/_deprecated/Activity';
import Lesson from '@/models/Lesson';
import { Views, bankTypes } from '@/utils/bank';
import { CogIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import { motion } from 'motion/react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConditionalRenderer from '../../ConditionalRenderer';
import MainButton, { MainButtonProps } from '../../buttons/MainButton';
import Modal, { ModalProps } from '../Modal';
import BackArrow from './BackArrow';
import ControlView from './ControlView';
import { FilterBank } from './FilterBank';
import { ListBankCards } from './ListBankCards';
import { SelectType } from './SelectType';

type UseFunction<T> = (item: T) => Promise<void> | void;

export type ExternalModalBankProps = {
  selectedId?: number;
  includedIds?: number[];
};

export type UseFunctions = {
  onUseActivity?: UseFunction<Activity>;
  onUseLesson?: UseFunction<Lesson>;
  onUseQuestion?: UseFunction<Activity>;
};
type ModalBankContentProps = {
  initial: bankTypes;
  loading?: boolean;
  isSlideMaterial?: boolean;
} & UseFunctions &
  ExternalModalBankProps;

export type ModalBankProps = ModalBankContentProps &
  Omit<ModalProps, 'children'>;

export default function ModalBank({
  initial,
  onUseActivity,
  onUseLesson,
  onUseQuestion,
  loading,
  selectedId,
  includedIds,
  isSlideMaterial,
  ...modalProps
}: ModalBankProps) {
  const modalBankProps = {
    initial,
    onUseActivity,
    onUseLesson,
    onUseQuestion,
    loading,
    selectedId,
    includedIds,
    isSlideMaterial,
  };

  return (
    <BankFiltersProvider>
      <Modal
        className={{ base: 'overflow-y-hidden h-[90vh] max-w-[80vw]' }}
        {...modalProps}
      >
        <ConditionalRenderer condition={modalProps.visible}>
          <ModalBankContent {...modalBankProps} />
        </ConditionalRenderer>
      </Modal>
    </BankFiltersProvider>
  );
}

function ModalBankContent({
  initial,
  onUseActivity,
  onUseLesson,
  onUseQuestion,
  loading,
  selectedId,
  isSlideMaterial,
  includedIds,
}: ModalBankContentProps) {
  const { reset } = useBankFilters();
  const [view, setView] = useState<Views>('list');
  const { t } = useTranslation('translation', {
    keyPrefix: 'modalBank',
  });

  const { user } = useAuth();

  const [activeContext, setActiveContext] = useState<bankTypes>(initial);
  const leftWidthAnimated = view === 'add' ? '50px' : '160px';

  const onClickManage = () => {
    setView('add');
    if (!isSuperAdmin(user?.userType)) {
      reset({ author: ['my'], inBank: undefined }, 'search');
    } else {
      reset({ inBank: undefined }, 'search');
    }
  };

  const onBack = () => {
    reset({ inBank: true }, 'search', 'author');
    setView('list');
  };

  return (
    <div className="relative flex w-full gap-6">
      <motion.aside
        className="border-r-2 border-base-200 pr-6 overflow-hidden sticky top-0 h-full"
        animate={{
          width: leftWidthAnimated,
        }}
      >
        <ControlView
          view={view}
          renders={{
            list: (
              <React.Fragment>
                <SelectType
                  activeContext={activeContext}
                  onSelected={setActiveContext}
                  isSlideMaterial={isSlideMaterial}
                />
                <FilterBank />
                <MainButton
                  text={t('manage')}
                  icon={<CogIcon />}
                  className="w-full whitespace-nowrap"
                  onClick={onClickManage}
                />
              </React.Fragment>
            ),
            add: <BackArrow onClick={onBack} />,
          }}
        />
      </motion.aside>

      <motion.section
        animate={{
          width: `calc(100% - ${leftWidthAnimated})`,
        }}
      >
        <ListBankCards
          mode={activeContext}
          checkable={view === 'add'}
          onUseActivity={onUseActivity}
          onUseLesson={onUseLesson}
          onUseQuestion={onUseQuestion}
          loading={loading}
          selectedId={selectedId}
          includedIds={includedIds}
        />
      </motion.section>
    </div>
  );
}

type ModalBankButtonProps = {
  mode: bankTypes;
} & Omit<MainButtonProps, 'text'>;

export const ModalBankButton = ({
  mode,
  ...buttonProps
}: ModalBankButtonProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'modalBank.openModal',
  });
  return (
    <MainButton
      text={t(mode)}
      icon={<RectangleStackIcon className="w-4" />}
      {...buttonProps}
    />
  );
};
